import { Card, Text, View } from "@src/components";
import type { WasteSenseKpis } from "@visioni/a3s-reports/client-messages";

interface WeeklySummaryProps {
  kpis: WasteSenseKpis;
}

interface KpiCardProps {
  label: string;
  value: string;
  helper?: string;
}

function KpiCard({ label, value, helper }: KpiCardProps) {
  return (
    <Card flex={1} gap="xs" minWidth={160}>
      <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
        {label}
      </Text>
      <Text textStyle="h2" color="text">
        {value}
      </Text>
      {helper ?
        <Text textStyle="xs/regular" color="textMuted">
          {helper}
        </Text>
      : null}
    </Card>
  );
}

/**
 * "Weekly summary" block — KPI grid. Numbers come pre-computed from the
 * generator so the renderer never disagrees with the email digest or the PDF.
 *
 * The redesign (2026-07) reframes the KPIs around media volume + operator-
 * confirmed evidence (clips, snapshots, confirmed dumps) rather than the old
 * AI event-funnel numbers.
 */
export function WeeklySummary({ kpis }: WeeklySummaryProps) {
  const fmt = (n: number) => n.toLocaleString();

  return (
    <View gap="md">
      <Text textStyle="h4" color="text">
        Weekly summary
      </Text>
      <View flexDirection="row" flexWrap="wrap" gap="md">
        <KpiCard label="Clips Recorded" value={fmt(kpis.clips_recorded)} helper="Total captured this week" />
        <KpiCard label="Snapshots Captured" value={fmt(kpis.snapshots_captured)} helper="Total captured this week" />
        <KpiCard label="Confirmed Dumps" value={fmt(kpis.confirmed_dumps)} helper="Confirmed by operators" />
      </View>
    </View>
  );
}
